/** SB-612: Initialize carousel semantics, static Tab stops and offscreen content in one
 * lifecycle. The theme/Splide still owns every slide change and control event.
 */
(() => {
  'use strict';
  if (window.sbCarouselAccessibility) return;
  window.sbCarouselAccessibility = true;
  const selector = 'loess-scroller, .rebuy-product-grid.splide';
  const staticSelector = '.rebuy-product-grid, .rebuy-product-block, .rebuy-cart, .splide[role="list"], .rebuy-money, .rebuy-product-price, .rebuy-product-price span, span.money, span.price, .card__price span, .product-price span';
  const states = new Map();
  /**
   * Set or remove an attribute only when needed to avoid observer feedback.
   * @param {Element} element - Element to update.
   * @param {string} name - Attribute name.
   * @param {string|null} value - Desired value, or null to remove it.
   */
  const set = (element, name, value) => {
    if (value === null) {
      if (element.hasAttribute(name)) element.removeAttribute(name);
    } else if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  };
  const interactive = 'a[href], button, input, select, textarea, [contenteditable="true"], [role="button"], [role="link"], [role="tab"], [role="checkbox"], [role="radio"], [role="slider"], [aria-controls], [aria-expanded]';

  /**
   * Remove an extra Tab stop from static content while preserving controls.
   * @param {Element|null} element - Candidate wrapper or price element.
   */
  function removeStaticStop(element) {
    if (element && !element.matches(interactive) && element.getAttribute('tabindex') === '0') {
      element.removeAttribute('tabindex');
    }
  }
  /**
   * Reconcile slide names, pagination and owned inert state with carousel geometry.
   * Preserve authored focus behavior and the keyboard route in control-free carousels.
   * @param {Object} state - Connected carousel root and its owned suppressed-slide set.
   */
  function refresh(state) {
    const { root } = state;
    if (!root.isConnected) return;
    const rebuy = root.matches('.rebuy-product-grid');
    const list = root.querySelector(rebuy ? '.splide__list' : 'ul');
    const viewport = rebuy ? root.querySelector('.splide__track') : list;
    if (!list || !viewport) return;
    const slides = Array.from(list.children).filter(slide => !slide.matches('script, style, template'));
    const originals = slides.filter(slide => !slide.classList.contains('splide__slide--clone'));
    const frame = viewport.getBoundingClientRect();
    // Native carousels without navigation controls rely on Tab scrolling their
    // links into view. Do not remove their only keyboard route to later cards.
    const navigationAvailable = rebuy || !!root.querySelector('loess-scroller-buttons button');

    removeStaticStop(root);
    removeStaticStop(list);
    set(list, 'role', 'presentation'); // Direct children are groups, not listitems.
    originals.forEach((slide, index) => {
      set(slide, 'role', 'group');
      set(slide, 'aria-roledescription', 'slide');
      // Preserve meaningful authored labels, replacing the provider's generic one.
      const label = slide.getAttribute('aria-label');
      if (!slide.hasAttribute('aria-labelledby') && (!label || label === 'product' || /^\d+ of \d+$/.test(label))) {
        set(slide, 'aria-label', (index + 1) + ' of ' + originals.length);
      }
      removeStaticStop(slide);
      slide.querySelectorAll('.rebuy-money, .rebuy-product-price, .rebuy-product-price span, span.money, span.price, .card__price span, .product-price span').forEach(removeStaticStop);
    });
    slides.forEach(slide => {
      const box = slide.getBoundingClientRect();
      // Test clipping against the carousel, not the page viewport: below-fold
      // content must already be reachable when approached with the keyboard.
      const visible = frame.width > 0 && box.width > 0 && box.height > 0 &&
        (!navigationAvailable || (box.right > frame.left + 1 && box.left < frame.right - 1)) &&
        !slide.classList.contains('splide__slide--clone') && slide.getAttribute('aria-hidden') !== 'true';
      if (!visible && !slide.contains(document.activeElement)) {
        if (!slide.hasAttribute('inert')) {
          state.suppressed.add(slide);
          slide.setAttribute('inert', '');
        }
      } else if (state.suppressed.delete(slide)) {
        slide.removeAttribute('inert');
      }
    });
    // Restore only our own attributes, even if a card is moved to another region.
    state.suppressed.forEach(slide => {
      if (!list.contains(slide)) {
        slide.removeAttribute('inert');
        state.suppressed.delete(slide);
      }
    });
    if (rebuy) {
      const pagination = root.querySelector('.splide__pagination');
      if (pagination) {
        set(pagination, 'role', 'group');
        set(pagination, 'aria-orientation', null);
        pagination.querySelectorAll('button').forEach(button => {
          set(button, 'role', null);
          set(button, 'aria-selected', null);
          set(button, 'aria-disabled', String(button.classList.contains('is-active')));
          set(button, 'tabindex', '0');
        });
      }
    }
  }
  /**
   * Coalesce carousel updates into one refresh per animation frame.
   * @param {Object} state - Carousel state holding its pending frame ID.
   */
  function schedule(state) {
    if (state.frame) return;
    state.frame = requestAnimationFrame(() => {
      state.frame = 0;
      refresh(state);
    });
  }
  /**
   * Observe a carousel once, or schedule a refresh for an existing connection.
   * @param {Element} root - Native scroller or Rebuy carousel element.
   */
  function connect(root) {
    if (states.has(root)) return schedule(states.get(root));
    const state = { root, suppressed: new Set(), frame: 0 };
    states.set(root, state);
    state.update = () => schedule(state);
    root.addEventListener('scroll', state.update, true);
    root.addEventListener('focusout', state.update);
    root.addEventListener('transitionend', state.update);
    state.observer = new MutationObserver(state.update);
    state.observer.observe(root, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'tabindex', 'role', 'aria-selected'] });
    if (window.ResizeObserver) {
      state.resize = new ResizeObserver(state.update);
      state.resize.observe(root);
    }
    schedule(state);
  }
  /**
   * Clean static Tab stops and connect carousels in an inserted or revealed subtree.
   * @param {Node} node - Mutation target or newly added subtree.
   */
  function discover(node) {
    if (node.nodeType !== 1) return;
    if (node.matches(staticSelector)) removeStaticStop(node);
    node.querySelectorAll(staticSelector).forEach(removeStaticStop);
    const root = node.closest(selector);
    if (root) connect(root);
    node.querySelectorAll(selector).forEach(connect);
  }
  /** Initialize discovery, resize updates and cleanup of detached carousel state. */
  function initialize() {
    discover(document.body);
    new MutationObserver(records => {
      records.forEach(record => {
        if (record.type === 'childList') record.addedNodes.forEach(discover);
        else if (record.attributeName === 'hidden' || record.target.matches('.rebuy-product-grid')) {
          discover(record.target); // Revealed tab, or a newly initialized provider.
        } else if (record.target.matches(staticSelector)) {
          removeStaticStop(record.target);
        }
      });
      states.forEach((state, root) => {
        if (root.isConnected) return;
        state.observer.disconnect();
        state.resize?.disconnect();
        cancelAnimationFrame(state.frame);
        root.removeEventListener('scroll', state.update, true);
        root.removeEventListener('focusout', state.update);
        root.removeEventListener('transitionend', state.update);
        state.suppressed.forEach(slide => slide.removeAttribute('inert'));
        states.delete(root);
      });
    }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'class', 'tabindex'] });
    window.addEventListener('resize', () => states.forEach(schedule), { passive: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
